import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { wrap: string; icon: LucideIcon; iconClass: string }> = {
  info: { wrap: 'border-info/25 bg-info/8 text-info', icon: Info, iconClass: 'text-info' },
  success: { wrap: 'border-success/25 bg-success/8 text-success', icon: CheckCircle2, iconClass: 'text-success' },
  warning: { wrap: 'border-warning/25 bg-warning/8 text-warning', icon: TriangleAlert, iconClass: 'text-warning' },
  danger: { wrap: 'border-danger/25 bg-danger/8 text-danger', icon: AlertCircle, iconClass: 'text-danger' },
};

interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Inline feedback. Errors announce assertively; everything else is polite, so a
 * background refresh doesn't interrupt a screen reader mid-sentence.
 */
export function Alert({ tone = 'info', title, children, action, onDismiss, className }: AlertProps) {
  const { wrap, icon: Icon, iconClass } = TONES[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', wrap, className)}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cn('text-fg-muted', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-mr-1 shrink-0 rounded p-1 text-fg-subtle transition-colors hover:text-fg"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
